/**
 * Recently viewed products list.
 *
 * Tracks product handles the visitor has recently opened in localStorage
 * and renders them as real theme product cards by calling Shopify's Section
 * Rendering API against a tiny render-only section
 * (`sections/recently-viewed-card-fragment.liquid`). That section just
 * invokes the theme's own `resource-card` snippet, so the cards rendered
 * here look identical to the theme's native product cards.
 *
 * This is intentionally independent from the theme's existing
 * `RecentlyViewed` utility (which stores IDs for its own internal uses)
 * so it can be reused inside the cart drawer without coupling to that
 * class.
 */

import { Component } from '@theme/component';

const STORAGE_KEY = 'theme_recently_viewed_v1';
const MAX_STORED = 20;
const CARD_SECTION_ID = 'recently-viewed-card-fragment';

function readHandles() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((h) => typeof h === 'string') : [];
  } catch (_error) {
    return [];
  }
}

function writeHandles(handles) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(handles.slice(0, MAX_STORED)));
  } catch (_error) {
    /* no-op */
  }
}

function trackHandle(handle) {
  if (!handle) return;
  const current = readHandles().filter((h) => h !== handle);
  current.unshift(handle);
  writeHandles(current);
}

/**
 * Fetches the rendered theme product card HTML for a given product handle.
 *
 * Uses Shopify's Section Rendering API against the render-only fragment
 * section. The API returns the section HTML wrapped in a
 * `<div id="shopify-section-<id>" class="shopify-section">...</div>`
 * which we return as-is; it is safe to inject into any container.
 *
 * @param {string} handle
 * @returns {Promise<string>}
 */
export async function fetchProductCardHTML(handle) {
  if (!handle) return '';

  const root = window.Shopify?.routes?.root ?? '/';
  const url = `${root.replace(/\/$/, '')}/products/${encodeURIComponent(handle)}?section_id=${CARD_SECTION_ID}`;

  try {
    const response = await fetch(url, { headers: { Accept: 'text/html' } });
    if (!response.ok) return '';
    const html = (await response.text()).trim();
    // Guard: if the fragment could not resolve a product / render a card it
    // returns (essentially) an empty section wrapper.
    if (!html || !/rv-card-fragment/i.test(html)) return '';
    return html;
  } catch (_error) {
    return '';
  }
}

/**
 * <recently-viewed-list>
 *
 * @extends {Component}
 */
class RecentlyViewedList extends Component {
  #hasRendered = false;

  connectedCallback() {
    super.connectedCallback();

    const currentHandle = this.dataset.currentHandle;
    if (currentHandle) trackHandle(currentHandle);

    this.#render();

    document.addEventListener('cart:refresh', this.#render);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('cart:refresh', this.#render);
  }

  /**
   * Called after the theme morphs this element's host section (e.g. when
   * the cart drawer re-renders after applying a discount). The subtree is
   * preserved via `data-skip-subtree-update`, but if for any reason the
   * list is currently empty after the morph we re-render so the visitor
   * never sees blank recently-viewed slots.
   */
  updatedCallback() {
    super.updatedCallback?.();
    const listEl = this.refs.list;
    if (this.#hasRendered && listEl instanceof HTMLElement && !listEl.innerHTML.trim()) {
      this.#render();
    }
  }

  get #limit() {
    return Number(this.dataset.limit || 4);
  }

  get #excludeHandle() {
    return this.dataset.currentHandle || '';
  }

  #render = async () => {
    const listEl = this.refs.list;
    if (!(listEl instanceof HTMLElement)) return;

    const excluded = new Set([this.#excludeHandle].filter(Boolean));
    const handles = readHandles()
      .filter((handle) => !excluded.has(handle))
      .slice(0, this.#limit);

    if (handles.length === 0) {
      this.setAttribute('data-empty', 'true');
      listEl.innerHTML = '';
      return;
    }

    const cards = await Promise.all(handles.map(fetchProductCardHTML));
    const html = cards.filter(Boolean).join('');

    if (!html.trim()) {
      this.setAttribute('data-empty', 'true');
      listEl.innerHTML = '';
      return;
    }

    this.removeAttribute('data-empty');
    listEl.innerHTML = html;
    this.#hasRendered = true;
  };
}

if (!customElements.get('recently-viewed-list')) {
  customElements.define('recently-viewed-list', RecentlyViewedList);
}
